import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  BookOpen,
  FolderOpen,
  Search,
  Edit,
  Trash2,
  Save,
  X,
  RefreshCw,
  ChevronDown,
  FileText,
  Tag,
  Calendar,
  User,
  Star,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";

export default function AddTopics() {
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);

  // Single state object for new topic
  const [newTopic, setNewTopic] = useState({
    topic: "",
    subjectId: "",
    chapterId: "",
    isPopular: false,
  });

  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Fetch chapters when subject is selected
  useEffect(() => {
    if (selectedSubjectId) {
      fetchChapters(selectedSubjectId);
    } else {
      setChapters([]);
      setSelectedChapterId("");
    }
  }, [selectedSubjectId]);

  // Fetch topics when both subject and chapter are selected
  useEffect(() => {
    if (selectedSubjectId && selectedChapterId) {
      fetchTopics(selectedSubjectId, selectedChapterId);
    } else {
      setTopics([]);
    }
  }, [selectedSubjectId, selectedChapterId]);

  // Update newTopic state when configuration changes
  useEffect(() => {
    if (selectedSubjectId) {
      setNewTopic((prev) => ({
        ...prev,
        subjectId: selectedSubjectId,
      }));
    }
  }, [selectedSubjectId]);

  useEffect(() => {
    if (selectedChapterId) {
      setNewTopic((prev) => ({
        ...prev,
        chapterId: selectedChapterId,
      }));
    }
  }, [selectedChapterId]);

  const fetchSubjects = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/subject/all`);
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
      console.error("Error fetching subjects:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (subjectId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: { subjectId },
      });
      setChapters(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch chapters");
      console.error("Error fetching chapters:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTopics = async (subjectId, chapterId) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/topics/all`, {
        params: { subjectId, chapterId },
      });
      setTopics(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch topics");
      console.error("Error fetching topics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.topic.trim()) {
      toast.warn("Please enter a topic name");
      return;
    }
    if (!newTopic.subjectId) {
      toast.warn("Please select a subject");
      return;
    }
    if (!newTopic.chapterId) {
      toast.warn("Please select a chapter");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.post(
        `${server}/topics/new`,
        {
          topic: newTopic.topic.trim(),
          subjectId: parseInt(newTopic.subjectId),
          chapterId: parseInt(newTopic.chapterId),
          isPopular: newTopic.isPopular,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Topic added successfully!");
      resetNewTopic();
      setShowAddModal(false);
      fetchTopics(selectedSubjectId, selectedChapterId);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add topic");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTopic = async (topic) => {
    if (!newTopic.topic.trim()) {
      toast.error("Please enter a topic name");
      return;
    }
    if (!newTopic.subjectId) {
      toast.error("Please select a subject");
      return;
    }
    if (!newTopic.chapterId) {
      toast.error("Please select a chapter");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(
        `${server}/topics/updateById`,
        {
          topic: newTopic.topic.trim(),
          subjectId: parseInt(newTopic.subjectId),
          chapterId: parseInt(newTopic.chapterId),
          isPopular: newTopic.isPopular,
        },
        {
          params: {
            topicId: topic.id,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Topic updated successfully!");
      setEditingTopic(null);
      resetNewTopic();
      fetchTopics(selectedSubjectId, selectedChapterId);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update topic");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm("Are you sure you want to delete this topic?")) {
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.delete(`${server}/topics/deleteById`, {
        params: {
          topicId: topicId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Topic deleted successfully!");
        fetchTopics(selectedSubjectId, selectedChapterId);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete topic");
    } finally {
      setSubmitting(false);
    }
  };

  const resetNewTopic = () => {
    setNewTopic({
      topic: "",
      subjectId: selectedSubjectId, // Keep the current subject selection
      chapterId: selectedChapterId, // Keep the current chapter selection
      isPopular: false,
    });
  };

  const startEdit = (topic) => {
    setEditingTopic(topic);
    setNewTopic({
      topic: topic.topic,
      subjectId: topic.subjectId?.toString(),
      chapterId: topic.chapterId?.toString(),
      isPopular: topic.isPopular || false,
    });
  };

  const cancelEdit = () => {
    setEditingTopic(null);
    resetNewTopic();
  };

  // Handle subject change in configuration
  const handleSubjectChange = (subjectId) => {
    setSelectedSubjectId(subjectId);
    // Reset chapter when subject changes
    setSelectedChapterId("");
  };

  // Handle chapter change in configuration
  const handleChapterChange = (chapterId) => {
    setSelectedChapterId(chapterId);
  };

  // Handle subject change in new topic form
  const handleNewTopicSubjectChange = (subjectId) => {
    setNewTopic((prev) => ({
      ...prev,
      subjectId,
      chapterId: "", // Reset chapter when subject changes
    }));

    // Also update configuration if we're not in edit mode
    if (!editingTopic) {
      setSelectedSubjectId(subjectId);
      setSelectedChapterId("");
    }
  };

  // Handle chapter change in new topic form
  const handleNewTopicChapterChange = (chapterId) => {
    setNewTopic((prev) => ({
      ...prev,
      chapterId,
    }));

    // Also update configuration if we're not in edit mode
    if (!editingTopic) {
      setSelectedChapterId(chapterId);
    }
  };

  // Filter topics based on search
  const filteredTopics = topics.filter((topic) =>
    topic.topic.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((s) => s.id === subjectId);
    return subject ? subject.subject : "Unknown Subject";
  };

  const getChapterName = (chapterId) => {
    const chapter = chapters.find((c) => c.id === chapterId);
    return chapter ? chapter.chapter : "Unknown Chapter";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Open add modal and set initial values from configuration
  const openAddModal = () => {
    setNewTopic((prev) => ({
      ...prev,
      subjectId: selectedSubjectId,
      chapterId: selectedChapterId,
    }));
    setShowAddModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl shadow-lg">
              <BookOpen className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Topic Manager</h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Manage topics for your subjects and chapters. Create, edit, and
            organize educational content.
          </p>
        </motion.div>

        {/* Configuration Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700"
        >
          <h2 className="text-xl font-semibold text-white mb-4">
            Configuration
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Subject Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subject
              </label>
              <div className="relative">
                <select
                  value={selectedSubjectId}
                  onChange={(e) => handleSubjectChange(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Chapter Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Chapter
              </label>
              <div className="relative">
                <select
                  value={selectedChapterId}
                  onChange={(e) => handleChapterChange(e.target.value)}
                  disabled={!selectedSubjectId}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Chapter</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.id} value={chapter.id}>
                      {chapter.chapter}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Topics Section */}
        {selectedSubjectId && selectedChapterId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {/* Controls */}
            <div className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="flex-1 w-full lg:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search topics..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() =>
                      fetchTopics(selectedSubjectId, selectedChapterId)
                    }
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                    />
                    Refresh
                  </button>

                  <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200"
                  >
                    <Plus className="w-4 h-4" />
                    Add Topic
                  </button>
                </div>
              </div>
            </div>

            {/* Topics List */}
            <div>
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
              ) : filteredTopics.length === 0 ? (
                <div className="text-center py-12 bg-gray-800 rounded-2xl border border-gray-700">
                  <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">
                    {searchTerm
                      ? "No topics match your search"
                      : "No topics found"}
                  </h3>
                  <p className="text-gray-500">
                    {searchTerm
                      ? "Try adjusting your search terms"
                      : "Get started by creating your first topic"}
                  </p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredTopics.map((topic) => (
                    <motion.div
                      key={topic.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-gray-600 transition-all duration-200"
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        {/* Topic Info */}
                        <div className="flex-1">
                          {editingTopic?.id === topic.id ? (
                            <div className="space-y-4">
                              <input
                                type="text"
                                value={newTopic.topic}
                                onChange={(e) =>
                                  setNewTopic({
                                    ...newTopic,
                                    topic: e.target.value,
                                  })
                                }
                                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Topic name..."
                                autoFocus
                              />
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                  <select
                                    value={newTopic.subjectId}
                                    onChange={(e) =>
                                      handleNewTopicSubjectChange(
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                                  >
                                    <option value="">Select Subject</option>
                                    {subjects.map((subject) => (
                                      <option
                                        key={subject.id}
                                        value={subject.id}
                                      >
                                        {subject.subject}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                </div>
                                <div className="relative">
                                  <select
                                    value={newTopic.chapterId}
                                    onChange={(e) =>
                                      handleNewTopicChapterChange(
                                        e.target.value
                                      )
                                    }
                                    disabled={!newTopic.subjectId}
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none disabled:opacity-50"
                                  >
                                    <option value="">Select Chapter</option>
                                    {chapters.map((chapter) => (
                                      <option
                                        key={chapter.id}
                                        value={chapter.id}
                                      >
                                        {chapter.chapter}
                                      </option>
                                    ))}
                                  </select>
                                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  id="editIsPopular"
                                  checked={newTopic.isPopular}
                                  onChange={(e) =>
                                    setNewTopic({
                                      ...newTopic,
                                      isPopular: e.target.checked,
                                    })
                                  }
                                  className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                                />
                                <label
                                  htmlFor="editIsPopular"
                                  className="text-sm text-gray-300 flex items-center gap-1"
                                >
                                  <Star className="w-4 h-4 text-yellow-400" />
                                  Popular Topic
                                </label>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <h3 className="text-xl font-semibold text-white">
                                  {topic.topic}
                                </h3>
                                {topic.isPopular && (
                                  <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">
                                    <Star className="w-3 h-3" />
                                    Popular
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                                <div className="flex items-center gap-1">
                                  <FolderOpen className="w-4 h-4" />
                                  <span>
                                    Subject: {getSubjectName(topic.subjectId)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Tag className="w-4 h-4" />
                                  <span>
                                    Chapter: {getChapterName(topic.chapterId)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  <span>{formatDate(topic.createdAt)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  <span>By User {topic.createdBy}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {editingTopic?.id === topic.id ? (
                            <>
                              <button
                                onClick={() => handleEditTopic(topic)}
                                disabled={submitting}
                                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                              >
                                <Save className="w-4 h-4" />
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={submitting}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => startEdit(topic)}
                                disabled={submitting}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                              >
                                <Edit className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTopic(topic.id)}
                                disabled={submitting}
                                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats */}
            {!loading && topics.length > 0 && (
              <div className="mt-6 text-center text-gray-400 text-sm">
                Showing {filteredTopics.length} of {topics.length} topics
                {searchTerm && " (filtered)"}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Add Topic Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Add New Topic
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleAddTopic} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Topic Name *
                  </label>
                  <input
                    type="text"
                    value={newTopic.topic}
                    onChange={(e) =>
                      setNewTopic({ ...newTopic, topic: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter topic name..."
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <div className="relative">
                    <select
                      value={newTopic.subjectId}
                      onChange={(e) =>
                        handleNewTopicSubjectChange(e.target.value)
                      }
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                      required
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.subject}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Chapter *
                  </label>
                  <div className="relative">
                    <select
                      value={newTopic.chapterId}
                      onChange={(e) =>
                        handleNewTopicChapterChange(e.target.value)
                      }
                      disabled={!newTopic.subjectId}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none disabled:opacity-50"
                      required
                    >
                      <option value="">Select Chapter</option>
                      {chapters.map((chapter) => (
                        <option key={chapter.id} value={chapter.id}>
                          {chapter.chapter}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPopular"
                    checked={newTopic.isPopular}
                    onChange={(e) =>
                      setNewTopic({ ...newTopic, isPopular: e.target.checked })
                    }
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label
                    htmlFor="isPopular"
                    className="text-sm text-gray-300 flex items-center gap-1"
                  >
                    <Star className="w-4 h-4 text-yellow-400" />
                    Mark as Popular Topic
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {submitting ? "Adding..." : "Add Topic"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
